import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Check, X } from "lucide-react";
import {
  apiRequest,
  eligibilityProblem,
  profileLabel,
  WIZARD_STEPS as steps,
} from "../utils/api";

const currentStep = 4;

// The route that renders Complete.js. Change it here if your router uses a
// different path.
const COMPLETE_ROUTE = "/complete";

const formatBytes = (n) => {
  if (n == null) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

function CheckRow({ ok, label, detail }) {
  return (
    <div className="flex items-start gap-3 py-2.5">
      <div
        className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
          ok ? "bg-green-500" : "bg-red-500"
        }`}
      >
        {ok ? <Check size={12} className="text-white" /> : <X size={12} className="text-white" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800">{label}</p>
        <p className="text-xs text-gray-500 mt-0.5 break-all">{detail}</p>
      </div>
    </div>
  );
}

const card = { background: "white", borderRadius: 12, padding: "20px", border: "1px solid #E2E8F0" };
const cardTitle = { fontSize: 14, fontWeight: 600, color: "#1F2937", margin: "0 0 14px 0" };

function DetailTable({ rows }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <tbody>
        {rows.map(([label, value, mono]) => (
          <tr key={label}>
            <td style={{ padding: "6px 0", fontSize: 14, color: "#9CA3AF", width: "40%", verticalAlign: "top" }}>
              {label}
            </td>
            <td
              style={{
                padding: "6px 0", fontSize: mono ? 12.5 : 14, color: "#374151",
                fontWeight: 500, wordBreak: "break-all",
                fontFamily: mono ? "monospace" : "inherit",
              }}
            >
              {value}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------
// Row builders and signing flow (pure / module-level)
// ---------------------------------------------------------------------
const STEP_STYLES = {
  done: { line: "bg-green-500", circle: "bg-green-500 text-white", text: "text-green-600" },
  current: { line: "bg-gray-300", circle: "bg-blue-600 text-white", text: "text-blue-600" },
  todo: { line: "bg-gray-300", circle: "bg-gray-200 text-gray-600", text: "text-gray-500" },
};

const stepStateOf = (index) => {
  if (index < currentStep) return "done";
  if (index === currentStep) return "current";
  return "todo";
};

const requestedLabel = (flag) => (flag ? "Requested" : "Not requested");

const buildArtifactRows = (manifest) => [
  ["File Name", manifest.firmware.file_name],
  ["File Size", formatBytes(manifest.firmware.file_size)],
  ["SHA-256", manifest.firmware.sha256, true],
  ["SHA-512", manifest.firmware.sha512, true],
];

const buildManifestRows = (manifest, manifestSha256) => [
  ["Manifest ID", manifest.manifest_id],
  ["Manifest SHA-256", manifestSha256 || "—", true],
  ["Created", manifest.created_at ? new Date(manifest.created_at).toLocaleString() : ""],
  ["Manifest Version", manifest.manifest_version],
];

const buildIdentityRows = (id, includeTSA, anchorBlockchain) => {
  if (!id) return [];
  return [
    ["Digital ID", `${id.identity_name} (#${id.id})`],
    ["Algorithm", id.algorithm],
    ["Signing Profile", profileLabel(id.identity_type)],
    ["Status", id.status],
    ["Valid", `${id.valid_from || "?"} to ${id.valid_till || "?"}`],
    ["Internal timestamp", requestedLabel(includeTSA)],
    ["Internal ledger entry", requestedLabel(anchorBlockchain)],
  ];
};

// Signs, then verifies straight away. A signing failure throws; a failure
// during verification is returned and shown on the result page - it is
// never reported as success.
const signAndVerify = async ({ artifactId, digitalId, profile, includeTSA, anchorBlockchain, onPhase }) => {
  onPhase("Signing…");
  const signResult = await apiRequest("/api/artifact/sign", {
    method: "POST",
    json: {
      artifact_id: artifactId,
      digital_id: digitalId,
      // The server rejects a profile that does not match the Digital ID.
      signing_profile: profile,
      include_tsa: !!includeTSA,
      blockchain_anchor: !!anchorBlockchain,
    },
  });

  let verification = null;
  let verificationError = "";
  try {
    onPhase("Verifying the signature…");
    verification = await apiRequest("/api/artifact/verify", {
      method: "POST",
      json: { artifact_id: artifactId, signature_id: signResult.signature_id },
    });
  } catch (err) {
    verificationError = err.message;
  }
  return { signResult, verification, verificationError };
};

const buildCompleteState = ({ artifactId, manifest, manifestSha256, selectedMethod, selectedDigitalId, includeTSA, anchorBlockchain, outcome }) => {
  const { signResult, verification, verificationError } = outcome;
  return {
    artifactId,
    manifest,
    manifestSha256: signResult.manifest_sha256 || manifestSha256,
    selectedMethod,
    selectedDigitalId,
    includeTSA,
    anchorBlockchain,

    signResult,
    verification,
    verificationError,

    // names the Downloads / Integration pages already read
    signature: signResult.signature,
    publicKey: signResult.public_key,
    algorithm: signResult.algorithm,
    hashAlgorithm: signResult.hash_algorithm,
    signedAt: signResult.signed_at,
    ipfsCid: signResult.ipfs_cid,
    ipfsUrl: signResult.ipfs_url,
  };
};

// ---------------------------------------------------------------------
// Presentational components
// ---------------------------------------------------------------------
const Stepper = () => (
  <div className="flex items-center justify-between relative mb-5" style={{ height: "72px" }}>
    {steps.map((step, index) => {
      const s = STEP_STYLES[stepStateOf(index)];
      return (
        <div key={step} className="flex-1 flex flex-col items-center relative">
          {index !== steps.length - 1 && (
            <div className={`absolute top-5 left-1/2 w-full h-[2px] ${s.line}`} />
          )}
          <div className={`z-10 rounded-full flex items-center justify-center text-xs font-semibold ${s.circle}`} style={{ width: "40px", height: "40px" }}>
            {index < currentStep ? <Check size={14} /> : index + 1}
          </div>
          <p className={`mt-2 text-[14px] font-medium text-center ${s.text}`}>{step}</p>
        </div>
      );
    })}
  </div>
);

const IdentityCard = ({ rows }) => (
  <div style={card}>
    <p style={cardTitle}>Signing Identity</p>
    {rows.length > 0 ? (
      <DetailTable rows={rows} />
    ) : (
      <p className="text-sm text-red-600">No Digital ID selected. Go back and choose one.</p>
    )}
  </div>
);

const ManifestCard = ({ manifest, rows }) => {
  const [show, setShow] = useState(false);
  return (
    <div style={card}>
      <p style={cardTitle}>Manifest</p>
      <DetailTable rows={rows} />
      <button
        onClick={() => setShow((v) => !v)}
        style={{
          marginTop: 10, fontSize: 13, color: "#2563EB", fontWeight: 500,
          background: "none", border: "none", padding: 0, cursor: "pointer",
          textDecoration: "underline",
        }}
      >
        {show ? "Hide manifest" : "View manifest"}
      </button>
      {show && (
        <pre
          style={{
            marginTop: 10, background: "#0F172A", color: "#E2E8F0", borderRadius: 8,
            padding: 12, fontSize: 11.5, overflowX: "auto", maxHeight: 280,
          }}
        >
          {JSON.stringify(manifest, null, 2)}
        </pre>
      )}
    </div>
  );
};

const PreSignChecks = ({ manifest, manifestSha256, identityProblem }) => (
  <div style={card}>
    <p style={cardTitle}>Before you sign</p>
    <div className="divide-y divide-gray-100">
      <CheckRow
        ok={!!manifest.firmware?.sha256}
        label="Artifact digest recorded by the server"
        detail="SHA-256 and SHA-512 were computed on the server from the stored file."
      />
      <CheckRow
        ok={!!manifest.manifest_id}
        label="Manifest generated and stored"
        detail={manifestSha256 ? `Manifest SHA-256 ${manifestSha256}` : "Manifest exists on the server."}
      />
      <CheckRow
        ok={!identityProblem}
        label="Digital ID can sign"
        detail={identityProblem || "Active, within its validity dates, and owned by your account."}
      />
    </div>
    <p className="text-xs text-gray-500 mt-3">
      Signing runs the server-side checks again. Right after signing, the signature, the
      manifest binding, the file digest, any timestamp or ledger entry, and the
      certificate record are verified automatically.
    </p>
  </div>
);

const AgreeCheckbox = ({ agreed, onToggle }) => (
  <label className="flex items-center gap-3 cursor-pointer select-none mt-5">
    <div
      onClick={onToggle}
      className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border transition ${agreed ? "bg-blue-600 border-blue-600" : "bg-white border-gray-300"}`}
    >
      {agreed && <Check size={12} className="text-white" />}
    </div>
    <span className="text-sm text-gray-700">
      I have reviewed the artifact, manifest and Digital ID details and agree to sign.
    </span>
  </label>
);

const SignError = ({ message }) => (
  <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mt-4">
    <p className="text-sm font-semibold text-red-700 mb-1">Signing did not complete</p>
    <p className="text-sm text-red-600">{message}</p>
    <p className="text-xs text-red-500 mt-1">No signature was recorded.</p>
  </div>
);

export default function ReviewArtifact() {
  const navigate = useNavigate();
  const location = useLocation();
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);
  const [phase, setPhase] = useState("");
  const [error, setError] = useState("");

  const {
    artifactId,
    manifest,
    manifestSha256,
    uploadedFiles,
    documentId,
    selectedMethod,
    selectedDigitalId,
    includeTSA,
    anchorBlockchain,
  } = location.state || {};

  const hasData = !!manifest && artifactId != null;
  const identityProblem = eligibilityProblem(selectedDigitalId);
  const canSign = hasData && !identityProblem && agreed && !signing;

  const handleSign = async () => {
    if (!canSign) return;
    setError("");
    setSigning(true);

    let outcome;
    try {
      outcome = await signAndVerify({
        artifactId,
        digitalId: selectedDigitalId.id,
        profile: selectedDigitalId.identity_type,
        includeTSA,
        anchorBlockchain,
        onPhase: setPhase,
      });
    } catch (err) {
      setSigning(false);
      setPhase("");
      setError(err.message);
      return;
    }

    navigate(COMPLETE_ROUTE, {
      state: buildCompleteState({
        artifactId, manifest, manifestSha256, selectedMethod,
        selectedDigitalId, includeTSA, anchorBlockchain, outcome,
      }),
    });
  };

  const goBack = () =>
    navigate("/signing-mode", {
      state: { artifactId, manifest, manifestSha256, uploadedFiles, documentId, includeTSA, anchorBlockchain },
    });

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <Stepper />

      {!hasData && (
        <div className="text-center py-16 text-gray-400 text-sm">
          No artifact data found. Please complete the previous steps first.
        </div>
      )}

      {hasData && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={card}>
                <p style={cardTitle}>Artifact</p>
                <DetailTable rows={buildArtifactRows(manifest)} />
              </div>
              <IdentityCard rows={buildIdentityRows(selectedDigitalId, includeTSA, anchorBlockchain)} />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <ManifestCard manifest={manifest} rows={buildManifestRows(manifest, manifestSha256)} />
              <PreSignChecks manifest={manifest} manifestSha256={manifestSha256} identityProblem={identityProblem} />
            </div>
          </div>

          <AgreeCheckbox agreed={agreed} onToggle={() => setAgreed((v) => !v)} />

          {error && <SignError message={error} />}
        </>
      )}

      <div className="flex justify-between mt-5">
        <button
          onClick={goBack}
          disabled={signing}
          className="text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 transition rounded-[10px] disabled:opacity-60"
          style={{ height: "44px", padding: "0 24px" }}
        >
          Back
        </button>
        <button
          onClick={handleSign}
          disabled={!canSign}
          className="text-sm font-medium text-white transition rounded-[10px]"
          style={{
            height: "44px", padding: "0 24px",
            background: canSign ? "#2563EB" : "#93C5FD",
            cursor: canSign ? "pointer" : "not-allowed",
          }}
        >
          {signing ? phase || "Working…" : "Sign Artifact"}
        </button>
      </div>
    </div>
  );
}